import { CurrencySwitcherCompact } from "#components/currency-switcher-compact"
import { LocaleSwitcherCompact } from "#components/locale-switcher-compact"
import { useTheme } from "#components/theme-provider"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "#components/ui/card"
import { Label } from "#components/ui/label"
import { useAccounts } from "#hooks/use-accounts"
import { useI18n } from "#hooks/use-i18n"
import { cardEntrance, fadeIn, getCardHoverEffect, staggerContainer } from "#lib/animations"
import { motion } from "framer-motion"
import { AccountMembersList } from "./account-members-list"

export function GeneralTab() {
  const { data: accounts = [], isLoading } = useAccounts()
  const { resolvedTheme } = useTheme()
  const { t } = useI18n()
  const isDark = resolvedTheme === "dark"

  // Separate owned and member accounts
  const ownedAccounts = accounts.filter(
    (account) => account.meta?.pivot_role?.toLowerCase() === "owner"
  )
  const memberAccounts = accounts.filter(
    (account) => account.meta?.pivot_role?.toLowerCase() !== "owner"
  )

  // Construct loading state
  if (isLoading) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-8">
        {t("common.loading")}
      </motion.div>
    )
  }

  // Construct owned account cards
  const ownedAccountCards = ownedAccounts.map((account, index) => (
    <motion.div
      key={account.id}
      variants={cardEntrance}
      whileHover={getCardHoverEffect(isDark)}
      transition={{ delay: index * 0.05 }}
    >
      <Card>
        <CardHeader>
          <CardTitle>
            {account.name}
            <span className="ml-2 text-xs text-muted-foreground">
              ({t("pages.settings.accountAccess.owner")})
            </span>
          </CardTitle>
          <CardDescription>{t("pages.settings.accountAccess.manageMembers")}</CardDescription>
        </CardHeader>
        <CardContent>
          <AccountMembersList accountId={account.id} />
        </CardContent>
      </Card>
    </motion.div>
  ))

  // Construct member account cards
  const memberAccountCards = memberAccounts.map((account, index) => (
    <motion.div
      key={account.id}
      variants={cardEntrance}
      whileHover={getCardHoverEffect(isDark)}
      transition={{ delay: (ownedAccounts.length + index) * 0.05 }}
    >
      <Card>
        <CardHeader>
          <CardTitle>
            {account.name}
            <span className="ml-2 text-xs text-muted-foreground">
              ({t("pages.settings.accountAccess.member")} - {account.meta?.pivot_role})
            </span>
          </CardTitle>
          <CardDescription>{t("pages.settings.accountAccess.memberDescription")}</CardDescription>
        </CardHeader>
        <CardContent>
          <AccountMembersList accountId={account.id} readOnly />
        </CardContent>
      </Card>
    </motion.div>
  ))

  // Construct empty state
  const emptyState =
    accounts.length === 0 ? (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-8">
        <p className="text-muted-foreground">{t("pages.settings.accountAccess.noAccounts")}</p>
      </motion.div>
    ) : null

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
      className="space-y-6"
    >
      <motion.div variants={fadeIn} className="space-y-4">
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">{t("pages.settings.preferences.title")}</h3>
          <div className="flex sm:flex-row flex-col justify-between gap-4">
            <div className="space-y-2 flex-1">
              <Label htmlFor="locale-select">{t("pages.settings.preferences.language")}</Label>
              <div className="sm:[&_button]:w-1/2 [&_button]:w-full">
                <LocaleSwitcherCompact />
              </div>
            </div>
            <div className="space-y-2 flex-1">
              <Label htmlFor="currency-select">{t("pages.settings.preferences.currency")}</Label>
              <div className="sm:[&_button]:w-1/2 [&_button]:w-full">
                <CurrencySwitcherCompact />
              </div>
            </div>
          </div>
        </div>
      </motion.div>
      <motion.div variants={fadeIn} className="space-y-4">
        <h3 className="text-lg font-semibold">{t("pages.settings.accountAccess.title")}</h3>
        {ownedAccountCards}
        {memberAccountCards}
        {emptyState}
      </motion.div>
    </motion.div>
  )
}
